// Gets the base URL for API calls from environment variable or defaults to current origin
export function getBaseUrl() {
  return process.env.REACT_APP_BACKEND_BASE_URL || window.location.origin;
}

const ACCESS_TOKEN_KEY = 'accessToken';
const REFRESH_TOKEN_KEY = 'refreshToken';

// The JWT lives in exactly one web storage at a time: localStorage when the user opted
// to be remembered (survives closing the browser) or sessionStorage otherwise (wiped
// when the tab/browser closes, so no credentials linger on a shared machine).

// Reads a token from whichever storage currently holds the session.
function readToken(key) {
  return sessionStorage.getItem(key) ?? localStorage.getItem(key);
}

// The storage that currently holds the session, or null when logged out. Lets a token
// refresh keep the tokens wherever the user's original "remember me" choice put them.
function currentStorage() {
  if (localStorage.getItem(ACCESS_TOKEN_KEY)) return localStorage;
  if (sessionStorage.getItem(ACCESS_TOKEN_KEY)) return sessionStorage;
  return null;
}

// Builds the Bearer auth header from the stored access token. Returns an empty string
// when there is no token so callers still send a well-formed request.
export function authHeader() {
  const token = readToken(ACCESS_TOKEN_KEY);
  return token ? `Bearer ${token}` : '';
}

// True when an access token is present, used to seed the app's auth state on load.
export function isAuthenticated() {
  return !!readToken(ACCESS_TOKEN_KEY);
}

// Persists a {accessToken, refreshToken} pair (mirrors the backend TokenResponse). The
// target storage defaults to wherever the session already lives (used by token refresh),
// falling back to sessionStorage; login passes it explicitly. The other storage is cleared
// so only a single copy of the tokens ever exists.
function storeTokens({ accessToken, refreshToken }, storage = currentStorage() || sessionStorage) {
  const other = storage === localStorage ? sessionStorage : localStorage;
  other.removeItem(ACCESS_TOKEN_KEY);
  other.removeItem(REFRESH_TOKEN_KEY);
  storage.setItem(ACCESS_TOKEN_KEY, accessToken);
  storage.setItem(REFRESH_TOKEN_KEY, refreshToken);
}

// Clears the stored tokens from both storages (logout, an explicit "delete my tokens",
// or when a refresh ultimately fails).
export function clearTokens() {
  localStorage.removeItem(ACCESS_TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
  sessionStorage.removeItem(ACCESS_TOKEN_KEY);
  sessionStorage.removeItem(REFRESH_TOKEN_KEY);
}

const REMEMBER_ME_KEY = 'rememberMe';

// Reads the persisted "remember me" choice so the login/register forms can restore
// the checkbox to the user's last preference. Only the boolean choice is stored (never
// credentials), so keeping it in localStorage across sessions is safe. Defaults to false.
export function getRememberPreference() {
  return localStorage.getItem(REMEMBER_ME_KEY) === 'true';
}

// Persists the "remember me" choice itself so the checkbox stays where the user left it.
export function setRememberPreference(remember) {
  localStorage.setItem(REMEMBER_ME_KEY, remember ? 'true' : 'false');
}

// Exchanges username/password for a token pair via POST /api/v0/auth/login and stores it.
// When `remember` is true the tokens go to localStorage so the session outlives the
// browser; otherwise they go to sessionStorage and are dropped when the browser closes.
// Throws on failure so the login form can surface the error.
export async function login(username, password, remember = false) {
  const response = await fetch(`${getBaseUrl()}/api/v0/auth/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ username, password }),
  });
  if (!response.ok) {
    throw new Error('Invalid username or password');
  }
  const tokens = await response.json();
  storeTokens(tokens, remember ? localStorage : sessionStorage);
  return tokens;
}

// Trades the stored refresh token for a fresh token pair via POST /api/v0/auth/refresh.
// Returns true on success; on failure it clears the tokens and signals a logout so the
// app can route back to the login page.
async function refreshAccessToken() {
  const refreshToken = readToken(REFRESH_TOKEN_KEY);
  if (!refreshToken) {
    return false;
  }
  const response = await fetch(`${getBaseUrl()}/api/v0/auth/refresh`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ refreshToken }),
  });
  if (!response.ok) {
    clearTokens();
    window.dispatchEvent(new Event('auth:logout'));
    return false;
  }
  storeTokens(await response.json());
  return true;
}

// Reads the reason out of an error response ({ error } — see the backend's StatusPages
// handler). Reads a clone so the caller can still consume the body itself. Returns '' when
// the body is absent or not the expected JSON, so a caller never depends on the wording.
async function errorMessage(response) {
  try {
    const { error } = await response.clone().json();
    return error || '';
  } catch {
    return '';
  }
}

// fetch wrapper that attaches the Bearer access token and, on a 401, transparently
// refreshes the token once and retries the request. The Authorization header is read
// fresh on each attempt so the retry picks up the rotated access token.
//
// The backend refuses every AI call with 402 once the user has spent their allowance
// (#206). That is a cross-cutting concern like the expired session above it: rather than
// leave each caller to recognise it, announce it once here and let the app run the
// feedback funnel (#208). The response is still returned, so callers keep their own
// error handling.
export async function authedFetch(url, options = {}) {
  const withAuth = () => ({
    ...options,
    headers: {
      ...(options.headers || {}),
      Authorization: authHeader(),
    },
  });

  let response = await fetch(url, withAuth());
  if (response.status === 401 && (await refreshAccessToken())) {
    response = await fetch(url, withAuth());
  }
  if (response.status === 402) {
    window.dispatchEvent(
      new CustomEvent('ai:capped', { detail: { message: await errorMessage(response) } }),
    );
  }
  return response;
}

// Serialises a LocaleDto ({ languageCode, countryCode }) into the LL_CC form the
// backend uses for the {personaLocale} path parameter (see Locale.localeRegex).
export function localeToIso(locale) {
  return `${locale.languageCode}_${locale.countryCode}`;
}

// Picks the student locale to pre-select in an "I speak" dropdown from the user's
// preferred language: an exact language+country match wins, otherwise the first
// locale sharing the preferred language code. Returns its ISO form, or '' when the
// preferred language is unknown or none of the offered locales match it (so the
// caller keeps its current — usually empty — selection).
export function defaultStudentLocaleIso(studentLocales, preferredLocale) {
  if (!preferredLocale || !preferredLocale.languageCode || studentLocales.length === 0) {
    return '';
  }
  const exact = studentLocales.find(
    (locale) => localeToIso(locale) === localeToIso(preferredLocale),
  );
  if (exact) {
    return localeToIso(exact);
  }
  const sameLanguage = studentLocales.find(
    (locale) => locale.languageCode === preferredLocale.languageCode,
  );
  return sameLanguage ? localeToIso(sameLanguage) : '';
}

// Converts an ISO 3166-1 alpha-2 country code into its flag emoji by mapping each
// letter to its regional indicator symbol (🇺 + 🇸 -> 🇺🇸). Returns the raw code if
// it is not a pair of letters.
export function countryFlag(countryCode) {
  if (!/^[A-Za-z]{2}$/.test(countryCode || '')) {
    return countryCode || '';
  }
  return countryCode
    .toUpperCase()
    .replace(/./g, (char) => String.fromCodePoint(0x1f1e6 + char.charCodeAt(0) - 65));
}

// Turns a LocaleDto into a label pairing the language name with the country's flag,
// such as "English 🇺🇸". Falls back to the language code when the runtime cannot
// resolve a display name.
export function formatLocale(locale) {
  const { languageCode, countryCode } = locale;

  let language;
  try {
    language = new Intl.DisplayNames(undefined, { type: 'language' }).of(languageCode);
  } catch {
    language = languageCode;
  }

  let region;
  try {
    region = new Intl.DisplayNames(undefined, { type: 'region' }).of(countryCode);
  } catch {
    region = countryCode;
  }

  return `${language} ${countryFlag(countryCode)} (${region})`;
}

// A compact variant of formatLocale pairing just the language name with the flag,
// such as "French 🇫🇷" — handy in tight spots like menu items where the region
// name would be too verbose.
export function formatLocaleShort(locale) {
  const { languageCode, countryCode } = locale;

  let language;
  try {
    language = new Intl.DisplayNames(undefined, { type: 'language' }).of(languageCode);
  } catch {
    language = languageCode;
  }

  return `${language} ${countryFlag(countryCode)}`.trim();
}

// Fetches every supported persona/student locale combination from /api/v0/locale.
// Mirrors dev.c15u.autolang.web.api.dto.SupportedLocalePairsResponse.
export async function fetchSupportedLocalePairs() {
  const response = await authedFetch(`${getBaseUrl()}/api/v0/locale`, {
    headers: {
      'Accept': 'application/json',
    },
  });
  if (!response.ok) {
    throw new Error(`Failed to load supported locales (${response.status})`);
  }
  const data = await response.json();
  return data.pairs || [];
}

// Translates a single message on demand via POST /api/v0/chat/{chatId}/translate?message_id=.
// The backend derives the translation direction from the message's author and the chat's
// locales, runs it through the LLM, stores it, and returns the translation. Mirrors
// dev.c15u.autolang.web.api.dto.discussion.TranslateMessageResponse
// ({ messageId, translation, translations }). `translations` holds one entry per model when the
// multi-AI tournament ran, else a single-element list; `translation` is the persisted first.
export async function translateMessage(chatId, messageId) {
  const params = new URLSearchParams({ message_id: String(messageId) });
  const response = await authedFetch(
    `${getBaseUrl()}/api/v0/chat/${encodeURIComponent(chatId)}/translate?${params}`,
    {
      method: 'POST',
      headers: {
        'Accept': 'application/json',
      },
    },
  );
  if (!response.ok) {
    throw new Error(`Failed to translate the message (${response.status})`);
  }
  return response.json();
}

// Fetches the personae available to the user from GET /api/v0/persona. Passing a
// name narrows the search server-side (see PersonaController.getPersonae); omitting
// it returns the full list. includePublic (default true) maps to include_public: when
// true the public personae are returned alongside the user's own, when false only the
// caller's own personae come back. includeArchived (default false) maps to
// include_archived; archived personae are omitted unless it is true, which is what
// keeps them out of chat creation.
// Mirrors dev.c15u.autolang.web.api.dto.persona.PersonaeResponse.
export async function fetchAllPersonae({ name, includePublic = true, includeArchived = false } = {}) {
  const params = new URLSearchParams({
    include_public: String(includePublic),
    include_archived: String(includeArchived),
  });
  if (name) {
    params.set('name', name);
  }
  const response = await authedFetch(`${getBaseUrl()}/api/v0/persona?${params}`, {
    headers: {
      'Accept': 'application/json',
    },
  });
  if (!response.ok) {
    throw new Error(`Failed to load personae (${response.status})`);
  }
  const data = await response.json();
  return data.personae || [];
}

// Creates a persona owned by the calling user via POST /api/v0/persona. The body
// mirrors dev.c15u.autolang.web.api.dto.persona.CreatePersonaRequest. Returns the
// created PersonaDto on success (HTTP 201).
export async function createPersona({ name, description, script, originalLocale, voice = null }) {
  const response = await authedFetch(`${getBaseUrl()}/api/v0/persona`, {
    method: 'POST',
    headers: {
      'Accept': 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ name, description, script, originalLocale, voice }),
  });
  if (!response.ok) {
    throw new Error(`Failed to create persona (${response.status})`);
  }
  return response.json();
}

// Archives a persona via PATCH /api/v0/persona/{id}/archive. The persona keeps its
// chats and stays readable by id; it simply drops out of the default listings.
// Idempotent server-side, so a repeated call is not an error.
export async function archivePersona(personaId) {
  const response = await authedFetch(`${getBaseUrl()}/api/v0/persona/${personaId}/archive`, {
    method: 'PATCH',
  });
  if (!response.ok) {
    throw new Error(`Failed to archive persona (${response.status})`);
  }
}

// Reverses archivePersona via PATCH /api/v0/persona/{id}/unarchive. Also idempotent.
export async function unarchivePersona(personaId) {
  const response = await authedFetch(`${getBaseUrl()}/api/v0/persona/${personaId}/unarchive`, {
    method: 'PATCH',
  });
  if (!response.ok) {
    throw new Error(`Failed to restore persona (${response.status})`);
  }
}

// Archives a chat via PATCH /api/v0/chat/{id}/archive. The chat drops out of the default
// chat listing but stays readable and writable by id, so an archived chat still opens and
// still accepts messages. Idempotent server-side, so a repeated call is not an error.
export async function archiveChat(chatId) {
  const response = await authedFetch(`${getBaseUrl()}/api/v0/chat/${chatId}/archive`, {
    method: 'PATCH',
  });
  if (!response.ok) {
    throw new Error(`Failed to archive chat (${response.status})`);
  }
}

// Reverses archiveChat via PATCH /api/v0/chat/{id}/unarchive. Also idempotent.
export async function unarchiveChat(chatId) {
  const response = await authedFetch(`${getBaseUrl()}/api/v0/chat/${chatId}/unarchive`, {
    method: 'PATCH',
  });
  if (!response.ok) {
    throw new Error(`Failed to restore chat (${response.status})`);
  }
}

// Sends one persona-builder turn to POST /api/v0/persona/discuss. The conversation
// is stateless on the server, so `messages` carries the whole transcript so far
// ([{ role: 'USER' | 'ASSISTANT', text }]) and `originalLocale` is the LocaleDto the
// persona will speak. Mirrors PersonaBuilderRequest/PersonaBuilderResponse; returns
// { reply, draft } where draft is null until the agent has gathered enough.
export async function discussPersona({ messages, originalLocale }) {
  const response = await authedFetch(`${getBaseUrl()}/api/v0/persona/discuss`, {
    method: 'POST',
    headers: {
      'Accept': 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ messages, originalLocale }),
  });
  if (!response.ok) {
    throw new Error(`Failed to reach the persona builder (${response.status})`);
  }
  return response.json();
}

// Fetches the student locales available for a given persona locale from
// /api/v0/locale/{personaLocale}. Mirrors AvailableStudentLocalesResponse.
export async function fetchStudentLocales(personaLocaleIso) {
  const response = await authedFetch(
    `${getBaseUrl()}/api/v0/locale/${encodeURIComponent(personaLocaleIso)}`,
    {
      headers: {
        'Accept': 'application/json',
      },
    },
  );
  if (!response.ok) {
    throw new Error(`Failed to load student locales (${response.status})`);
  }
  const data = await response.json();
  return data.studentLocales || [];
}

// What this deployment serves, from GET /api/v0/capabilities. Mirrors
// dev.c15u.autolang.web.api.dto.CapabilitiesDto, whose characterStub says whether the development
// panel — and the screens that only exist beside it — are served here. Asked before there is
// anything to play: a round in progress says the same thing, but a screen has to know whether to
// open at all.
export async function fetchCapabilities() {
  const response = await authedFetch(`${getBaseUrl()}/api/v0/capabilities`, {
    headers: {
      'Accept': 'application/json',
    },
  });
  if (!response.ok) {
    throw new Error(`Failed to read what this deployment serves (${response.status})`);
  }
  return response.json();
}

// Fetches the current user's profile from /api/v0/user/profile. Mirrors
// dev.c15u.autolang.web.api.dto.user.UserProfileDto, whose preferredLocale is a
// LocaleDto ({ languageCode, countryCode }) or null when the user has not chosen one.
export async function fetchUserProfile() {
  const response = await authedFetch(`${getBaseUrl()}/api/v0/user/profile`, {
    headers: {
      'Accept': 'application/json',
    },
  });
  if (!response.ok) {
    throw new Error(`Failed to load user profile (${response.status})`);
  }
  return response.json();
}

// Persists the user's preferred UI language as a full LocaleDto via
// PUT /api/v0/user/profile. Mirrors
// dev.c15u.autolang.web.api.dto.user.config.UpdateUserProfileRequest.
export async function updatePreferredLocale(locale) {
  const response = await authedFetch(`${getBaseUrl()}/api/v0/user/profile`, {
    method: 'PUT',
    headers: {
      'Accept': 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ preferredLocale: locale }),
  });
  if (!response.ok) {
    throw new Error(`Failed to save preferred language (${response.status})`);
  }
  return response.json();
}

// The CEFR proficiency levels, ordered from beginner to mastery. Mirrors the
// backend dev.c15u.autolang.business.CefrLevel enum.
export const CEFR_LEVELS = ['A1', 'A2', 'B1', 'B2', 'C1', 'C2'];

// Replaces the whole set of languages the user is studying via
// PUT /api/v0/user/studied-languages. `languages` is an array of
// { languageCode, level } (StudiedLanguageDto); the request always carries the
// complete list. Returns the refreshed UserProfileDto.
export async function updateStudiedLanguages(languages) {
  const response = await authedFetch(`${getBaseUrl()}/api/v0/user/studied-languages`, {
    method: 'PUT',
    headers: {
      'Accept': 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ languages }),
  });
  if (!response.ok) {
    throw new Error(`Failed to save studied languages (${response.status})`);
  }
  return response.json();
}

// Submits feedback (#207) via POST /api/v0/feedback. Every field is optional; the
// amount is sent as a decimal string to keep monetary precision. Blank fields are sent
// as empty strings, which the backend treats as "not provided". Throws on failure so the
// screen can surface an error and stay on the form.
export async function submitFeedback({ amount, currency, comment, email } = {}) {
  const response = await authedFetch(`${getBaseUrl()}/api/v0/feedback`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ amount, currency, comment, email }),
  });
  if (!response.ok) {
    throw new Error(`Failed to submit feedback (${response.status})`);
  }
}

// What to open the feedback amount field with (#231): the caller's accrued AI spend and the
// proposed amount in each currency the server can convert. The currency list comes from here
// rather than from the browser, so the picker cannot offer something the server has no rate for.
export async function fetchFeedbackSuggestion() {
  const response = await authedFetch(`${getBaseUrl()}/api/v0/feedback/suggestion`, {
    method: 'GET',
  });
  if (!response.ok) {
    throw new Error(`Failed to fetch the feedback suggestion (${response.status})`);
  }
  return response.json();
}

// Reads back the caller's own feedback (#233) via GET /api/v0/feedback, newest first. The
// backend answers with the caller's submissions and nobody else's, so there is nothing to
// scope here. Throws on failure so the screen can keep the form usable and say nothing rather
// than show a list it could not load.
export async function listOwnFeedback() {
  const response = await authedFetch(`${getBaseUrl()}/api/v0/feedback`, {
    headers: {
      'Accept': 'application/json',
    },
  });
  if (!response.ok) {
    throw new Error(`Failed to load your feedback (${response.status})`);
  }
  return response.json();
}

// Records that the user closed the invitation to give feedback via
// POST /api/v0/feedback/prompt/dismissal. The server is the only record of a dismissal, so a
// cleared cache or another device cannot resurrect an ask the user already refused. Best-effort
// from the caller's point of view: the banner hides on the click, and a failure here simply means
// the ask returns on the next load rather than costing the user their place in a lesson.
export async function dismissFeedbackPrompt() {
  const response = await authedFetch(`${getBaseUrl()}/api/v0/feedback/prompt/dismissal`, {
    method: 'POST',
  });
  if (!response.ok) {
    throw new Error(`Failed to dismiss the feedback invitation (${response.status})`);
  }
}

// Opens a voice session against a persona and returns { ticket, model }. The relay's WebSocket
// cannot carry an Authorization header, so this call exchanges the bearer token for a short-lived
// single-use ticket, and that ticket is the whole credential for the socket that follows.
//
// The persona is named here rather than over the socket: it is what the session is configured from,
// and only this leg of the handshake knows who is asking. A persona the caller cannot see is
// answered with 404, which the status on the thrown error is what lets the screen tell apart.
export async function startVoiceSession(personaId) {
  const response = await authedFetch(`${getBaseUrl()}/api/v0/voice/session`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ personaId }),
  });
  if (!response.ok) {
    const error = new Error(`Failed to start a voice session (${response.status})`);
    error.status = response.status;
    throw error;
  }
  return response.json();
}

// Where the relay socket lives, ticket included. Same origin as the API, same scheme family.
export function voiceStreamUrl(ticket) {
  const base = getBaseUrl().replace(/^http/, 'ws');
  return `${base}/api/v0/voice/stream?ticket=${encodeURIComponent(ticket)}`;
}

// The scenes a scenario walks, via GET /api/v0/scenario/{key}: in the order it walks them, each
// saying whether the caller has ever reached it. What the door may offer is decided from this
// and from nothing else — where a student has been is the server's word, not the browser's.
export async function fetchScenarioScenes(scenarioKey) {
  const response = await authedFetch(
    `${getBaseUrl()}/api/v0/scenario/${encodeURIComponent(scenarioKey)}`,
    {
      headers: {
        'Accept': 'application/json',
      },
    },
  );
  if (!response.ok) {
    throw new Error(`Could not read what the scenario walks (${response.status})`);
  }
  return response.json();
}

// Every door a run is opened at: a POST answering with the run it opened, and refusing the same
// way whichever of them was knocked on. `said` is what the door is told on the way in.
//
// A conversation needs to know what language the student already speaks, and no form asks during a
// scene: it comes from their profile. A profile that does not say is answered with 428, which
// the status on the thrown error is what lets the screen tell apart — it asks once, and the server
// remembers the answer.
async function openARun(url, said) {
  const response = await authedFetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(said),
  });
  if (!response.ok) {
    const error = new Error(`Failed to start a scenario run (${response.status})`);
    error.status = response.status;
    throw error;
  }
  return response.json();
}

// Records that the student has started playing a scenario, via POST /api/v0/scenario/{key}/run.
// It opens the scenario's first scene, and the run it answers with is what every later call names.
export const startScenarioRun = (scenarioKey, studentLocale, bahn) =>
  openARun(
    `${getBaseUrl()}/api/v0/scenario/${encodeURIComponent(scenarioKey)}/run`,
    { ...(studentLocale ? { studentLocale } : {}), ...(bahn ? { bahn } : {}) },
  );

// The same, for a student coming back to a scenario part-way along, via
// POST /api/v0/scenario/{key}/scene/{sceneKey}/run: the run opens standing in the scene named and
// the ones before it are passed on the way. Refused where the student has never been, so
// only a scene the door offered is worth asking for.
export const startScenarioRunAt = (scenarioKey, sceneKey, bahn) =>
  openARun(
    `${getBaseUrl()}/api/v0/scenario/${encodeURIComponent(scenarioKey)}` +
      `/scene/${encodeURIComponent(sceneKey)}/run`,
    bahn ? { bahn } : {},
  );

// The ticket for speaking to one character of a run, via
// POST /api/v0/scenario/run/{runId}/session. The role crosses the wire, never the persona:
// the cast is seeded, and a browser carrying a character's UUID could be handed another one.
export async function startSceneVoiceSession(runId, role) {
  const response = await authedFetch(
    `${getBaseUrl()}/api/v0/scenario/run/${encodeURIComponent(runId)}/session`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ role }),
    },
  );
  if (!response.ok) {
    const error = new Error(`Failed to start a voice session (${response.status})`);
    error.status = response.status;
    throw error;
  }
  return response.json();
}

// The ticket for hearing what the run is announced over, via
// POST /api/v0/scenario/run/{runId}/announcer/session. It names nobody: there is nobody in it to
// talk to, and the run is the whole of what it is opened against.
export async function startAnnouncerSession(runId) {
  const response = await authedFetch(
    `${getBaseUrl()}/api/v0/scenario/run/${encodeURIComponent(runId)}/announcer/session`,
    { method: 'POST' },
  );
  if (!response.ok) {
    const error = new Error(`Failed to start an announcer session (${response.status})`);
    error.status = response.status;
    throw error;
  }
  return response.json();
}

// The ticket for asking the teacher mid-scene, via
// POST /api/v0/scenario/run/{runId}/teacher/session. The role names whose conversation the
// teacher reads and where the exchange is recorded — never who the teacher is.
export async function startSceneTeacherSession(runId, role) {
  const response = await authedFetch(
    `${getBaseUrl()}/api/v0/scenario/run/${encodeURIComponent(runId)}/teacher/session`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ role }),
    },
  );
  if (!response.ok) {
    const error = new Error(`Failed to start a teacher session (${response.status})`);
    error.status = response.status;
    throw error;
  }
  return response.json();
}

// Everything the student has played, via GET /api/v0/scenario/record. The tree comes back already
// grouped and ordered — scenario, then run, then scene, then conversation — so the screen shapes
// nothing.
export async function fetchSceneRecord() {
  const response = await authedFetch(`${getBaseUrl()}/api/v0/scenario/record`, {
    headers: {
      'Accept': 'application/json',
    },
  });
  if (!response.ok) {
    throw new Error(`Could not read the scene record (${response.status})`);
  }
  return response.json();
}

// The conversations of one run, scene by scene, in the order they were played. Read out of the
// record, which the server has already grouped and ordered — a run nothing was said in is in no
// group at all, and reads here as no conversations.
export async function fetchRunConversations(runId) {
  const record = await fetchSceneRecord();
  return (record.scenarios || [])
    .flatMap((scenario) => scenario.runs || [])
    .filter((run) => run.runId === runId)
    .flatMap((run) => run.scenes || [])
    .flatMap((scene) => scene.conversations || []);
}

/** Which side of a conversation a recorded turn was said from. */
export const actorToSender = (actor) => (actor === 'STUDENT' ? 'user' : 'persona');

// A conversation as a transcript draws it, out of what the chat door answers with. One place
// rather than one per screen: a chat and a scene conversation are the same rows, and two readings
// of them would be two chances to draw the same turn differently.
//
// Mirrors dev.c15u.autolang.web.api.dto.chat.ChatHistoryResponse
export const historyToMessages = (data) =>
  (data.history || []).map((entry) => ({
    id: entry.id,
    text: entry.text,
    translation: entry.translation,
    sender: actorToSender(entry.actor),
    annotations: entry.teacherEntry?.annotations || [],
    // Where the student stopped hearing a spoken answer, when they cut it off.
    skipped: entry.skipped ?? null,
    comment: entry.teacherEntry?.shortAnswer || null,
    correction: entry.teacherEntry?.correction || null,
  }));

// One conversation, via GET /api/v0/chat/{chatId}: what was said, and what the teacher read into
// it. A scene conversation is an ordinary chat, so it is read back through the ordinary chat door
// and comes with the annotations any other chat carries.
export async function fetchChatHistory(chatId) {
  const response = await authedFetch(
    `${getBaseUrl()}/api/v0/chat/${encodeURIComponent(chatId)}`,
    {
      headers: {
        'Accept': 'application/json',
      },
    },
  );
  if (!response.ok) {
    throw new Error(`Could not read the conversation (${response.status})`);
  }
  return historyToMessages(await response.json());
}

// The run in progress, as the student may see it, via GET /api/v0/scenario/run/{runId}: its
// clock, and the scene it is in under `scene`. Throws when there is no round under that id — a
// run somebody else started, or one that never was. A restart is no longer one of the reasons:
// the round is written down with the run.
export async function fetchScenarioRun(runId) {
  const response = await authedFetch(
    `${getBaseUrl()}/api/v0/scenario/run/${encodeURIComponent(runId)}`,
    {
      headers: {
        'Accept': 'application/json',
      },
    },
  );
  if (!response.ok) {
    throw new Error(`No round in progress (${response.status})`);
  }
  return response.json();
}

// Every press a run answers to: all are POST /api/v0/scenario/run/{runId}/{move}, most with nothing
// to say, all refuse with 409 when the run is not where the move starts from, and all but one
// answer with the run in the shape reading it gives. `said` is what a move that names something
// carries; a move that changes nothing the student can see answers 204, and this answers null.
async function moveTheRun(runId, move, refused, said = null) {
  const response = await authedFetch(
    `${getBaseUrl()}/api/v0/scenario/run/${encodeURIComponent(runId)}/${move}`,
    {
      method: 'POST',
      headers: {
        'Accept': 'application/json',
        ...(said ? { 'Content-Type': 'application/json' } : {}),
      },
      ...(said ? { body: JSON.stringify(said) } : {}),
    },
  );
  if (!response.ok) {
    throw new Error(`${refused} (${response.status})`);
  }
  return response.status === 204 ? null : response.json();
}

// The student walking into the scene the run is in, out of the briefing that describes it. Where
// the run is standing was written down when it walked in; this is the student catching up with it,
// and the one move that reads no run back — what a scene does about somebody being there, a station
// calling a platform change over their head, is heard rather than drawn.
export const enterScene = (runId) =>
  moveTheRun(runId, 'enter-scene', 'Could not walk into the scene');

// The student leaving the scene they are in. Deliberately not a character action: the way out has
// to work where no character is served, which is every production deployment. Refused while the
// scene's own rules say it is not over.
export const finishScene = (runId) =>
  moveTheRun(runId, 'finish-scene', 'Could not leave the scene');

// The student walking out of the run itself, from whichever scene they are standing in and from
// the gap between two of them. Its own door rather than the way out of a scene: that one leaves a
// scene for the interstitial and the run walks on. Nothing gates it, and a run already over keeps
// the ending it has, so pressing it twice is one walk out.
export const leaveScenarioRun = (runId) =>
  moveTheRun(runId, 'leave', 'Could not walk out');

// The student pressing on out of the interstitial, into whatever the run has left to walk, and out
// of the run itself when nothing is. Refused from a scene that has not been left.
export const advanceScenario = (runId) =>
  moveTheRun(runId, 'advance', 'Could not press on');

// The clock a run is played against, stopped while the student is doing something that is not
// playing and started again when they go back to it. Both are idempotent, so a hold taken twice is
// the one hold and a release nobody took changes nothing.
export const holdScenarioClock = (runId) =>
  moveTheRun(runId, 'hold', 'Could not stop the clock');

export const releaseScenarioClock = (runId) =>
  moveTheRun(runId, 'release', 'Could not start the clock again');

// What the student themselves does in the scene the run is in: the platform they walk to at a
// departure board. `action` is one field per scene, filled in for the one the run is in, and a
// scene handed another's answers 400.
//
// Deliberately not a character action: this is a student pressing something, so it is served by
// every deployment rather than only by one standing in for somebody who is meant to be spoken to.
export const sceneAction = (runId, action) =>
  moveTheRun(runId, 'scene-action', 'The scene did not take that', action);

// Does what the character in a run does, via POST
// /api/v0/scenario/run/{runId}/character-action: the name of a tool the scene declares, and the
// arguments it takes. Dispatched through the handler the character's own call goes through, and
// answered with what that handler answered — what the character is told, and the frame the screen
// is sent. Only served while the character stub is enabled, so a 404 here means the deployment
// does not offer the development panel.
export async function characterAction(runId, name, args) {
  const response = await authedFetch(
    `${getBaseUrl()}/api/v0/scenario/run/${encodeURIComponent(runId)}/character-action`,
    {
      method: 'POST',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ name, arguments: args }),
    },
  );
  if (!response.ok) {
    throw new Error(`The character did not answer (${response.status})`);
  }
  return response.json();
}

// What the character in a run knows and may do, via GET
// /api/v0/scenario/run/{runId}/character-view. Throws on failure rather than answering null: an
// expired session or a round the server has forgotten is not the same thing as a deployment that
// serves no panel, and the round itself already says which deployment this is.
export async function fetchCharacterView(runId) {
  const response = await authedFetch(
    `${getBaseUrl()}/api/v0/scenario/run/${encodeURIComponent(runId)}/character-view`,
    {
      headers: {
        'Accept': 'application/json',
      },
    },
  );
  if (!response.ok) {
    throw new Error(`Could not read what the character knows (${response.status})`);
  }
  return response.json();
}
